import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { PlusIcon } from '@heroicons/react/24/outline';
import SettingsPanel from '@/components/core/SettingsPanel';
import { confirm } from '@/components/core/createConfirmation';
import { createPathwayCustomProperty, deletePathwayCustomProperty, updatePathwayCustomProperty } from '@/api/pathways';
import { parseErrorMessage } from '@/utils/errorUtils';
import { stringify } from '@/utils/miscUtils';
import { paramActions } from '@/stores/paramStore';
import { pathwayActions, usePathwayCustomProperties } from '@/stores/pathwayStore';
import { toast } from 'react-toastify';
import DraftPropertyRow from './DraftPropertyRow';
import SavedPropertyRow from './SavedPropertyRow';
import { unitToString } from '@/utils/unitUtils';
import { useSpeciesData } from '@/components/registry/hooks/useSpeciesData';

const EMPTY_OPTION_VALUE = '';

const EMPTY_UNIT_OPTION = {
  value: EMPTY_OPTION_VALUE,
  label: '—',
};

const getUnitSignature = unit =>
  stringify({
    numerator: unit?.numerator ?? [],
    denominator: unit?.denominator ?? [],
  });

const buildUnitOptions = unitsByType => {
  const unitsByKey = new Map();

  unitsByType.forEach(units => {
    units.forEach(unit => {
      if (!unit) {
        return;
      }

      const key = getUnitSignature(unit);

      if (!unitsByKey.has(key)) {
        unitsByKey.set(key, {
          value: key,
          label: unitToString(unit),
          unit: {
            numerator: unit?.numerator ?? [],
            denominator: unit?.denominator ?? [],
          },
        });
      }
    });
  });

  return Array.from(unitsByKey.values())
    .filter(option => option.label)
    .sort((a, b) => a.label.localeCompare(b.label));
};

const buildPropertyUnit = (numerator, denominator) => ({
  numerator: numerator?.numerator ?? [],
  denominator: denominator?.value === EMPTY_OPTION_VALUE ? [] : (denominator?.numerator ?? []),
});

const CustomPropertiesPanel = () => {
  const { pathwayId } = useParams();
  const customProperties = usePathwayCustomProperties();
  const [showDraft, setShowDraft] = useState(false);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftNumerator, setDraftNumerator] = useState(EMPTY_OPTION_VALUE);
  const [draftDenominator, setDraftDenominator] = useState(EMPTY_OPTION_VALUE);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingPropertyName, setDeletingPropertyName] = useState(null);
  const [updatingPropertyName, setUpdatingPropertyName] = useState(null);
  const { typeToUnitsMap, isLoading: isUnitOptionsLoading } = useSpeciesData();

  const unitOptions = useMemo(() => buildUnitOptions(Object.values(typeToUnitsMap ?? {})), [typeToUnitsMap]);

  const unitOptionsByValue = useMemo(
    () =>
      unitOptions.reduce((acc, option) => {
        acc[option.value] = option.unit;
        return acc;
      }, {}),
    [unitOptions],
  );

  const resetDraft = () => {
    setShowDraft(false);
    setDraftLabel('');
    setDraftNumerator(EMPTY_OPTION_VALUE);
    setDraftDenominator(EMPTY_OPTION_VALUE);
  };

  const onCreate = async () => {
    const trimmedLabel = draftLabel.trim();

    if (!trimmedLabel) {
      toast.error('Custom property label is required.');
      return;
    }

    if (draftNumerator === EMPTY_OPTION_VALUE) {
      toast.error('Select a numerator unit.');
      return;
    }

    const numerator = unitOptionsByValue[draftNumerator];
    const denominator =
      draftDenominator === EMPTY_OPTION_VALUE ? EMPTY_UNIT_OPTION : unitOptionsByValue[draftDenominator];

    if (!numerator) {
      toast.error('Select a valid numerator unit.');
      return;
    }

    setIsCreating(true);
    paramActions.setSavePending(true);

    try {
      const { data } = await createPathwayCustomProperty(pathwayId, {
        label: trimmedLabel,
        unit: buildPropertyUnit(numerator, denominator),
      });
      pathwayActions.setCustomProperties([...customProperties, data]);
      resetDraft();
      paramActions.setSavePending(false);
    } catch (error) {
      paramActions.setSavePending(null);
      parseErrorMessage(error).forEach(message => toast.error(message));
    } finally {
      setIsCreating(false);
    }
  };

  const onUpdate = async (property, data) => {
    setUpdatingPropertyName(property.name);
    paramActions.setSavePending(true);

    try {
      const { data: updated } = await updatePathwayCustomProperty(pathwayId, property.name, data);
      pathwayActions.setCustomProperties(customProperties.map(item => (item.name === property.name ? updated : item)));
      paramActions.setSavePending(false);
      return true;
    } catch (error) {
      paramActions.setSavePending(null);
      parseErrorMessage(error).forEach(message => toast.error(message));
      return false;
    } finally {
      setUpdatingPropertyName(null);
    }
  };

  const onDelete = async property => {
    const ok = await confirm(
      `Delete "${property.label}"?\nThis also removes saved values for this property from all case nodes in the pathway.`,
    );

    if (!ok) {
      return;
    }

    setDeletingPropertyName(property.name);
    paramActions.setSavePending(true);

    try {
      await deletePathwayCustomProperty(pathwayId, property.name);
      pathwayActions.setCustomProperties(customProperties.filter(item => item.name !== property.name));
      paramActions.setSavePending(false);
    } catch (error) {
      paramActions.setSavePending(null);
      parseErrorMessage(error).forEach(message => toast.error(message));
    } finally {
      setDeletingPropertyName(null);
    }
  };

  return (
    <SettingsPanel title="Custom Properties">
      <div className="space-y-4">
        {customProperties.map(property => (
          <SavedPropertyRow
            key={property.name}
            property={property}
            isDeleting={deletingPropertyName === property.name}
            isUpdating={updatingPropertyName === property.name}
            onDelete={() => onDelete(property)}
            onUpdate={onUpdate}
          />
        ))}

        {showDraft && (
          <DraftPropertyRow
            label={draftLabel}
            numeratorValue={draftNumerator}
            denominatorValue={draftDenominator}
            unitOptions={unitOptions}
            unitOptionsLoading={isUnitOptionsLoading}
            onLabelChange={setDraftLabel}
            onNumeratorChange={setDraftNumerator}
            onDenominatorChange={setDraftDenominator}
            onCreate={onCreate}
            onCancel={resetDraft}
            isCreating={isCreating}
          />
        )}

        {!showDraft && (
          <button
            type="button"
            className="btn btn-sm btn-ghost w-full rounded-xl border border-dashed border-gray-300 bg-white text-base normal-case hover:bg-gray-50"
            onClick={() => setShowDraft(true)}
          >
            <PlusIcon className="h-5 w-5" />
            Add Custom Property
          </button>
        )}
      </div>
    </SettingsPanel>
  );
};

export default CustomPropertiesPanel;
